import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Quote, PenLine } from "lucide-react";
import axios from "axios";
import { useShop } from "../context/ShopContext";
import ReviewForm from "./ReviewForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ count = 5 }) => (
  <div className="flex gap-1 text-olive-dark">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={14} fill={i < count ? "currentColor" : "none"} strokeWidth={i < count ? 0 : 1.5} />
    ))}
  </div>
);

const ReviewCard = ({ item, i, featured = false, emptyLabel }) => (
  <motion.figure
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, delay: 0.1 + (i % 4) * 0.12, ease: [0.16, 1, 0.3, 1] }}
    whileHover={{ y: -5 }}
    className={`bg-cream border border-brown/10 flex flex-col justify-between relative overflow-hidden ${
      featured ? "lg:col-span-2 p-10 md:p-14" : "p-8 md:p-10"
    }`}
    data-testid={featured ? "testimonial-featured" : `testimonial-card-${i}`}
  >
    {featured && (
      <Quote
        size={120}
        strokeWidth={0.5}
        className="absolute -top-4 -right-4 text-olive/15 rotate-12 pointer-events-none"
      />
    )}
    <div className="relative">
      <Stars count={item.rating} />
      <blockquote
        className={
          featured
            ? "mt-8 font-serif text-2xl sm:text-3xl lg:text-4xl font-medium leading-snug text-brown"
            : "mt-6 text-base font-light leading-relaxed text-brown-light"
        }
      >
        “{item.comment}”
      </blockquote>
      {item.photo_path && (
        <img
          src={`${API}/reviews/${item.id}/photo`}
          alt={`${item.name} — müşteri fotoğrafı`}
          loading="lazy"
          className={`mt-5 w-full object-cover border border-brown/10 ${featured ? "h-48" : "h-36"}`}
          data-testid={`testimonial-photo-${i}`}
        />
      )}
    </div>
    <figcaption className={`relative flex items-center gap-3 ${featured ? "mt-12" : "mt-8"}`}>
      <span
        className={`rounded-full bg-olive/20 text-olive-dark font-serif flex items-center justify-center ${
          featured ? "w-12 h-12 text-xl" : "w-10 h-10 text-lg"
        }`}
      >
        {item.name.charAt(0)}
      </span>
      <p className="text-sm font-medium text-brown">{item.name}</p>
    </figcaption>
  </motion.figure>
);

const Testimonials = () => {
  const { t, cval, lang } = useShop();
  const d = t.testimonials;
  const [approved, setApproved] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/reviews`)
      .then((r) => setApproved(r.data || []))
      .catch(() => setApproved([]));
  }, []);

  const list = approved || [];
  const avg =
    list.length > 0
      ? `${(list.reduce((s, r) => s + r.rating, 0) / list.length).toFixed(1)}/5`
      : null;
  const featured = list[0];
  const rest = list.slice(1);

  return (
    <section
      id="testimonials"
      className="bg-cream-dark py-24 md:py-36 border-y border-brown/10"
      data-testid="testimonials-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16 md:mb-20 flex flex-col md:flex-row md:items-end md:justify-between gap-6"
        >
          <div>
            <p className="text-xs tracking-[0.3em] uppercase font-semibold text-olive-dark mb-4">
              {d.chapter}
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-brown">
              {cval(`testimonials_title_${lang}`, d.title)}
            </h2>
          </div>
          <div className="flex items-center gap-6">
            {avg && (
              <div className="flex items-center gap-4">
                <p className="font-serif text-4xl md:text-5xl font-medium text-brown" data-testid="rating-score">
                  {avg}
                </p>
                <div>
                  <Stars count={Math.round(parseFloat(avg))} />
                  <p className="mt-1 text-xs tracking-wide uppercase text-brown-muted">
                    {list.length} {t.reviews.countLabel}
                  </p>
                </div>
              </div>
            )}
            <button
              onClick={() => setFormOpen(true)}
              className="hidden sm:inline-flex items-center gap-2 px-6 py-3 rounded-full border border-brown/30 text-brown text-sm font-medium tracking-wide hover:border-olive hover:text-olive-dark transition-colors duration-300"
              data-testid="write-review-button"
            >
              <PenLine size={15} /> {t.reviews.writeReview}
            </button>
          </div>
        </motion.div>

        {list.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8 }}
            className="border border-dashed border-brown/20 py-16 px-8 text-center"
            data-testid="testimonials-empty"
          >
            <p className="font-serif text-2xl text-brown-muted italic">{t.reviews.emptyPublic}</p>
            <button
              onClick={() => setFormOpen(true)}
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-olive text-cream text-sm font-medium tracking-wide hover:bg-olive-dark transition-colors duration-300"
              data-testid="write-review-button-empty"
            >
              <PenLine size={15} /> {t.reviews.writeReview}
            </button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {featured && <ReviewCard item={featured} i={0} featured />}
            {rest.map((item, i) => (
              <ReviewCard key={item.id} item={item} i={i + 1} />
            ))}
          </div>
        )}

        <div className="mt-10 sm:hidden">
          <button
            onClick={() => setFormOpen(true)}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full border border-brown/30 text-brown text-sm font-medium tracking-wide"
            data-testid="write-review-button-mobile"
          >
            <PenLine size={15} /> {t.reviews.writeReview}
          </button>
        </div>
      </div>

      <ReviewForm open={formOpen} onOpenChange={setFormOpen} />
    </section>
  );
};

export default Testimonials;
